import React, { useId } from 'react';

/**
 * A yes/no switch - the control for a setting that is either on or off and takes effect as one
 * fact, not a form field. A checkbox says "tick to include"; a switch says "this is on".
 *
 * `role="switch"` + `aria-checked` so a screen reader announces it as a switch and reads its state.
 * Space and Enter are handled here rather than left to the button's native activation: a native
 * button already clicks on those keys, so handling them AND leaving activation alone would toggle
 * twice per keypress. preventDefault on keydown suppresses the native click in every browser.
 */
export default function ToggleSwitch({ checked, onChange, disabled = false, label, helper, id }) {
  const autoId = useId();
  const switchId = id || autoId;
  const labelId = `${switchId}-label`;
  const helperId = helper ? `${switchId}-helper` : undefined;

  const toggle = () => {
    if (disabled) return;
    onChange(!checked);
  };

  const onKeyDown = (e) => {
    if (e.key === ' ' || e.key === 'Enter' || e.key === 'Spacebar') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        id={switchId}
        role="switch"
        aria-checked={checked ? 'true' : 'false'}
        aria-labelledby={label ? labelId : undefined}
        aria-describedby={helperId}
        disabled={disabled}
        onClick={toggle}
        onKeyDown={onKeyDown}
        className={`relative shrink-0 w-[38px] h-[22px] rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#0089ff] focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
          checked ? 'bg-[#007a55]' : 'bg-[#cbd5e1]'
        }`}
      >
        <span
          aria-hidden="true"
          className={`absolute top-[2px] left-[2px] w-[18px] h-[18px] rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-[16px]' : 'translate-x-0'
          }`}
        />
      </button>
      {(label || helper) && (
        <div className="min-w-0">
          {label && (
            <span
              id={labelId}
              onClick={toggle}
              className={`block text-[13px] font-medium text-[#0f172a] ${disabled ? '' : 'cursor-pointer'}`}
            >
              {label}
            </span>
          )}
          {helper && (
            <p id={helperId} className="text-[12px] text-[#64748b] mt-0.5">
              {helper}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
